<script>
import _isObject from 'lodash/isObject';
import _orderBy from 'lodash/orderBy';
import _map from 'lodash/map';

import DateLabel from '@/js/components/common/labels/DateLabel';
import UserLabel from '@/js/components/user/UserLabel';

import formatDateTime from '@/js/helpers/format-date-time';
import htmlEntities from '@/js/helpers/htmlentities';

const fieldSortOrder = [
    'publication',
    'code',
    'title',
    'language_id',
    'received_at',
    'activity',
    'state_id',
    'edition',
    'authors',
    'authors_emails',
    'authors_language_id',
    'figures',
    'equations',
    'appendix',
    'pages',
    'filesystem',
    'notes',
    'user',
    'accepted_at',
];

const validStates = ['awaiting', 'working', 'pending', 'completed', 'error'];
const validLanguages = ['en', 'es', 'fr', 'pt'];

const defaultFormatter = function (value, trans) {
    if (!value) {
        return trans('logs.rows.no-value');
    }

    return htmlEntities(value).replace(/\s+/gm, ' ');
};

const languageFormatter = function (value, trans) {
    return (validLanguages.indexOf(value) > -1)
        ? trans(`common.languages.${value}`)
        : trans('logs.rows.unknown');
};

const stateFormatter = function (value, trans) {
    return (validStates.indexOf(value) > -1)
        ? trans(`common.states.${value}`)
        : trans('logs.rows.unknown');
};

const fieldFormatter = function (value, trans) {
    return (fieldSortOrder.indexOf(value) > -1)
        ? trans(`logs.rows.fields.${value}`)
        : value;
};

const nameFormatter = function (value, trans) {
    if (!value) {
        return trans('logs.rows.no-value');
    }

    return defaultFormatter(value.name, trans);
};

const dateFormatter = function (value, trans) {
    if (!value) {
        return trans('logs.rows.no-value');
    }

    return formatDateTime(value, trans('common.date-format'));
};

const multiLineFormatter = function (value, trans) {
    if (!value) {
        return trans('logs.rows.no-value');
    }

    return defaultFormatter(value.split(/[\r\n]+/gm).join(', '), trans);
};

const arrayFormatter = function (value, trans) {
    if (!value || !Array.isArray(value) || (value.length === 0)) {
        return trans('logs.rows.no-value');
    }

    return defaultFormatter(value.join(', '), trans);
};

const activityFormatter = function (activity, trans) {
    if (activity.user !== undefined) {
        return trans('logs.rows.activity-with-user', {
            activity: activity.name,
            state: stateFormatter(activity.state_id, trans),
            user: activity.user ? activity.user.name : trans('logs.rows.no-user'),
        });
    }

    return trans('logs.rows.activity', {
        activity: activity.name,
        state: stateFormatter(activity.state_id, trans),
    });
};

const userFormatter = function (user, trans) {
    if (!user) {
        return trans('logs.rows.no-user');
    }

    return defaultFormatter(user.name, trans);
};

const editionFomatter = function (edition, trans) {
    return edition && edition.name || trans('logs.rows.no-edition');
};

const formatters = {
    'publication': nameFormatter,
    'activity': activityFormatter,
    'edition': editionFomatter,
    'user': userFormatter,
    'state_id': stateFormatter,
    'received_at': dateFormatter,
    'accepted_at': dateFormatter,
    'language_id': languageFormatter,
    'authors': multiLineFormatter,
    'authors_emails': arrayFormatter,
    'authors_language_id': languageFormatter,
};

const parseEditedPayload = function (original, current, showField, trans) {
    const getOriginalValue = !_isObject(original)
        ? () => false
        : (key) => {
            const formatter = formatters[key] || defaultFormatter;

            return trans('logs.rows.from', {from: formatter(original[key], trans)});
        };

    const results = _map(
        _orderBy(
            _map(current, (newValue, key) => {
                const formatter = formatters[key] || defaultFormatter;
                const sortIndex = fieldSortOrder.indexOf(key);

                return {
                    data: [
                        showField && fieldFormatter(key, trans),
                        getOriginalValue(key),
                        trans('logs.rows.to', {to: formatter(newValue, trans)}),
                    ].filter((value) => !!value),

                    sortIndex: (sortIndex === -1 ? 999 : sortIndex),
                };
            }),
            'sortIndex',
        ),
        (value) => value.data.join(' / '),
    );

    return results.join(' | ');
};

const parseActionPayload = function (action, reason, trans) {
    return [
        trans('logs.rows.action', {action: action.name}),
        (reason === null)
            ? null
            : trans('logs.rows.reason', {reason: htmlEntities(reason)}),
    ].filter((value) => !!value).join(' / ');

};

const parsePayload = function (type, payload, trans) {
    if (payload === null || ['added', 'message'].includes(type)) {
        return null;
    }

    if (['edited', 'changed-edition', 'changed-activity', 'changed-user'].includes(type)) {
        const {'from': original, to: current} = payload;

        return parseEditedPayload(original, current, type === 'edited', trans);
    }

    if (type === 'changed-tags') {
        const {to: tags} = payload;

        if (tags.length === 0) {
            return trans('tags.nil');
        }

        return tags.map((tag) => `[${tag}]`).join(', ');
    }

    if (type === 'perfomed-action') {
        const {action, reason} = payload;

        return parseActionPayload(action, reason, trans);
    }

    if (['archived', 'unarchived', 'destroyed', 'restored'].includes(type)) {
        return payload.reason;
    }

    return null;
};

export default {
    name: 'LogRow',

    functional: true,

    props: {
        log: {type: Object, required: true},
    },

    render(createElement, {props, parent}) {
        const trans = (key, payload = {}) => parent.$t(key, payload);

        const payload = parsePayload(props.log.type, props.log.payload, trans);

        return createElement('tr', [
            createElement('th', {staticClass: 'align-top text-nowrap'}, [
                createElement('span', {staticClass: 'd-block text-muted'}, props.log.document.code),
                createElement(DateLabel, {props: {date: props.log.created_at}}),
            ]),

            createElement('td', {staticClass: 'align-top'}, [
                createElement('div', {staticClass: 'text-prewrap'}, [props.log.message]),
                (payload)
                    ? createElement('em', {staticClass: 'd-block', domProps: {innerHTML: payload}})
                    : null,
            ]),

            createElement('td', {staticClass: 'align-top'}, [
                createElement(UserLabel, {
                    props: {
                        user: props.log.user,
                        none: '-',
                    },
                }),
            ]),
        ]);
    },
};
</script>
